import {
  useViewChecklistsSuspense,
  type ViewChecklistsRequest,
  type ViewChecklistsResponse
} from "@swivu/client-api";
import { createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { groupBy } from "../utils/groupBy.js";
import { AccordionV2 } from "./AccordionV2.js";
import { Badge } from "./Badge.js";
import ErrorBoundary from "./ErrorBoundary.js";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";
import { cn } from "../utils/cn.js";

type ViewChecklistsVariables = ViewChecklistsRequest & {
  baseLink?: string;
};
type ViewChecklistsProps = ViewChecklistsVariables & {
  checklists: ViewChecklistsResponse;
};

const ViewChecklistsContext = createContext<ViewChecklistsProps | null>(null);

const ViewChecklistsProviderWrapper = ({
  children,
  ...props
}: ViewChecklistsVariables & { children: React.ReactNode }) => {
  return (
    <SuspenseWithDelay fallback={<ViewChecklistsSkeleton />}>
      <ErrorBoundary>
        <ViewChecklistsProvider {...props}>{children}</ViewChecklistsProvider>
      </ErrorBoundary>
    </SuspenseWithDelay>
  );
};

function hasGroupValue<T extends { _group_value: unknown }>(
  result: T
): result is T & { _group_value: string } {
  return typeof result._group_value === "string";
}

const ViewChecklistsProvider = ({
  children,
  ...props
}: ViewChecklistsVariables & { children: React.ReactNode }) => {
  const { data: checklists } = useViewChecklistsSuspense({
    variables: props
  });
  return (
    <ViewChecklistsContext.Provider value={{ ...props, checklists }}>
      {children}
    </ViewChecklistsContext.Provider>
  );
};

const useViewChecklists = () => {
  const context = useContext(ViewChecklistsContext);
  if (!context) {
    throw new Error(
      "useViewChecklists must be used within a ViewChecklistsProvider"
    );
  }
  return context;
};

const ViewChecklists = () => {
  const { checklists, id, baseLink = "/checklists" } = useViewChecklists();
  const isGummy = useIsGummy();

  const resultsWithoutNull = checklists
    .map((result) => ({
      ...result,
      _group_value: result._group_value ?? "undefined"
    }))
    .filter(hasGroupValue);

  const grouped = groupBy(resultsWithoutNull, "_group_value");
  const results = Object.fromEntries(
    Object.entries(grouped).sort((a, b) => {
      return (b[1]?.[0]?.order ?? 0) - (a[1]?.[0]?.order ?? 0);
    })
  );
  // return <pre>{JSON.stringify(results, null, 2)}</pre>;
  return (
    <div>
      <AccordionV2.PersistentRoot
        persistKey={`view-Checklists-${id}`}
        defaultValue={["undefined"]}
      >
        {Object.entries(results).map(([key, Checklists]) => (
          <AccordionV2.Item key={key} value={key ?? "undefined"}>
            <div className="bg-mono-100 dark:bg-mono-950 sticky top-[0px] z-10 -mx-2 px-2">
              {key !== "undefined" && (
                <AccordionV2.Trigger className="w-full">
                  <h3 className="dark:border-mono-700/50 flex gap-x-1  py-2 text-lg">
                    <div className="font-bold">
                      <span>{key}</span>
                    </div>
                    <Badge
                      count={Checklists?.[0]._group_count ?? 0}
                      action="info"
                      className="static -translate-y-1"
                    />
                  </h3>
                </AccordionV2.Trigger>
              )}
            </div>
            <AccordionV2.Content>
              <dd className="accordion-content grid gap-x-8 gap-y-6 pt-2 md:block md:space-y-6">
                {Checklists.map((checklist) => (
                  <dl
                    key={checklist.id}
                    className={cn(
                      "overflow-hidden",
                      isGummy
                        ? cn(
                            "rounded-surface-md bg-white dark:bg-mono-900",
                            "shadow-card"
                          )
                        : "dark:bg-mono-950 rounded-md bg-white shadow"
                    )}
                  >
                    <Link
                      to={`${baseLink}/${checklist.id}`}
                      className={cn(
                        "flex gap-x-2 pr-2 md:gap-x-4",
                        isGummy
                          ? "rounded-surface-md"
                          : "dark:border-mono-700/50 border-mono-200 rounded-md border"
                      )}
                    >
                      <span
                        className={cn(
                          "flex-none",
                          isGummy
                            ? "w-1.5 rounded-l-[18px]"
                            : "w-1 rounded-l-md"
                        )}
                      />
                      <div className="flex flex-1 items-center justify-between gap-x-4">
                        <div className="flex-1 py-2">
                          <div className="group flex items-center gap-4">
                            <h5
                              className={cn(
                                "text-lg",
                                isGummy ? "font-extrabold" : "font-bold"
                              )}
                            >
                              {checklist.title}
                            </h5>
                          </div>
                          <p className="leading-4 opacity-70">
                            {checklist.entity_name}
                          </p>
                        </div>
                        <Badge
                          count={checklist.unchecked_items_count ?? 0}
                          action="info"
                          className="size-12 static p-3 text-base"
                        />
                      </div>
                    </Link>
                  </dl>
                ))}
              </dd>
            </AccordionV2.Content>
          </AccordionV2.Item>
        ))}
      </AccordionV2.PersistentRoot>
    </div>
  );
};

const ViewChecklistsSkeleton = () => {
  return (
    <ul className={`animate-pulse space-y-6 pt-2`}>
      {Array.from({ length: 2 }).map((_, key) => {
        return (
          <li key={key} className={`space-y-3`}>
            <div className={``}>
              <h3
                className={`bg-mono-300  dark:bg-mono-800 border-mono-300 dark:border-mono-700 h-10 rounded-md border-b  pb-2  pt-6 text-lg font-bold `}
              />
            </div>
            <dd
              className={` grid gap-x-8 gap-y-6 pt-2 md:block  md:space-y-6 `}
            >
              {Array.from({ length: 2 * (key + 1) }).map((_, i) => (
                <dl key={i} className={``}>
                  <div
                    className={`bg-mono-200 dark:bg-mono-900 h-10 rounded-md`}
                  />
                </dl>
              ))}
            </dd>
          </li>
        );
      })}
    </ul>
  );
};

ViewChecklists.Provider = ViewChecklistsProviderWrapper;
ViewChecklists.useViewChecklists = useViewChecklists;
ViewChecklists.Skeleton = ViewChecklistsSkeleton;
ViewChecklists.displayName = "ViewChecklists";

export default ViewChecklists;
